import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
  applyTextShadowToElements,
} from "../utils/text_treatment"
import { TYPE_PAIR_SPECS } from "../services/language_learning_type_pairs"

// Live-preview controller for the Substitution Drill wizard.
//
// The preview mirrors FRAME 0, and on this template frame 0 is the PAYOFF pole:
// the complete frame sentence with an EMPTY slot box, its gloss, and ALL N
// substitutions painted in full ink with their meanings, under the header
// (flag, language, ×N count pill, i/N step pill, DRILL badge). Every applier
// here therefore REPAINTS something that is already visible. Four consequences,
// each a law rather than a style choice:
//
//   1. Nothing here may touch `opacity`, ever. The template has no fade-in and
//      no fade-out anywhere: the per-beat swap is a hard cut plus a
//      translate/scale settle at constant opacity, and the row walk is a colour
//      and background change. There is no opacity axis to animate.
//   2. Nothing here fills the slot. The slot is EMPTY at frame 0 and beat 1
//      drops the first substitution into it about two seconds in — that is the
//      drill's prompt state, not a withhold (every candidate occupant is
//      already painted in the grid below it). A preview that pre-filled it
//      would be previewing the §6 Q2 variant we did not build.
//   3. Nothing here lights a row. The active-row walk starts on beat 1; a lit
//      row at frame 0 would show a video that has already begun.
//   4. The SUBSTITUTIONS are deliberately NOT live-bound. Two derived
//      quantities depend on ALL of them at once — the grid's fitted row height
//      (N rows into a fixed budget) and the slot box's static width (pinned to
//      the WIDEST option so it never resizes per beat) — and both are computed
//      server-side from the same numbers the renderer uses. A JS copy would be
//      a third derivation, which is precisely how a preview starts lying about
//      the render, so row edits repaint on save (the T1/T2/T3/T8 posture).
//      Everything that does NOT feed those two fits — hook, frame, frame gloss,
//      CTA, the two languages, theme, typography, size, stroke, shadow —
//      updates live.
//
// The FRAME is live-bound and split here exactly as the builder splits it
// (`partition` at the placeholder, no trimming): the author's own spacing is
// what makes the sentence read correctly around the box, and the placeholder
// itself never becomes ink. That split is a one-liner over one field, not a
// derivation over the whole payload — which is why it is safe to mirror where
// the option grid is not.
//
// The two languages are asymmetric, as everywhere in the LL kit:
// `target_language_code` is the language of everything VOICED (hero flag +
// language label) and `learner_locale` is the language of everything READ
// (glosses, badge) — which is why it also repaints the badge and the label.
//
// TYPE_PAIR_SPECS is imported from the shared language-learning catalog
// (de-mirrored — the font data is authored once in the TS source and consumed by
// both legs), so the preview can never drift from what the renderer paints.
//
// Mounted alongside `palette-live-preview`, which owns the background layers +
// tint/darkness sliders. Inherits event wiring + dispatch + value normalisation
// from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    "frame", "flag", "languageLabel", "drillBadge", "countPill", "stepPill",
    "hookText",
    "frameLine", "frameBefore", "frameAfter", "slotBox", "frameGloss",
    "optionText", "optionGloss",
    "ctaText",
  ]

  static FIELD_BINDINGS = {
    hook_text:            { apply: "applyHookText" },
    frame_text:           { apply: "applyFrameText" },
    frame_gloss:          { apply: "applyFrameGloss" },
    cta_text:             { apply: "applyCtaText" },
    target_language_code: { apply: "applyTargetLanguage" },
    learner_locale:       { apply: "applyLearnerLocale" },
    theme:                { apply: "applyTheme" },
    type_pair:            { apply: "applyTypography" },
    italic_override:      { apply: "applyTypography" },
    font_size_scale:      { apply: "applyFontSize" },
    display_override:     { apply: "applyFontSize" },
    body_override:        { apply: "applyFontSize" },
    text_shadow:          { apply: "applyTextShadow" },
    text_stroke_color:    { apply: "applyStroke" },
    text_stroke_width:    { apply: "applyStroke" },
  }

  // -- chrome ---------------------------------------------------------------

  applyHookText() {
    this.setText("hookText", stripLanguageTags(this.fieldValue("hook_text")) || "Your hook goes here.")
  }

  applyCtaText() { this.setText("ctaText", this.fieldValue("cta_text")) }

  // The frame, SPLIT at the slot — `partition`, not `split`, and no trimming:
  // "Quiero " and ", por favor." are what make the sentence read correctly
  // around the box. Mirrors SubstitutionDrill::ConfigBuilder#frame_parts, which
  // emits the same two halves as `frameBefore` / `frameAfter`.
  //
  // The slot itself is never given content here (law 2). A frame with no
  // placeholder puts the whole sentence in the before-half and leaves the box
  // standing empty at the end of the line — which is exactly what the wizard
  // should show, because the render REFUSES that config.
  applyFrameText({ value }) {
    const [ before, , after ] = partition(stripLanguageTags(value), PLACEHOLDER)
    this.setText("frameBefore", before)
    this.setText("frameAfter", after)
  }

  applyFrameGloss({ value }) { this.setText("frameGloss", value) }

  // The VOICED language: the hero flag and the language label. Base subtag
  // throughout, so "en-GB" flags as 🇬🇧 — matching
  // SubstitutionDrill::ConfigBuilder#resolved_target_flag_emoji, which splits
  // the subtag before the lookup.
  applyTargetLanguage({ value }) {
    this.setText("languageLabel", languageName(value, this.learnerLocale()))
    if (this.hasFlagTarget) this.flagTarget.textContent = FLAGS[baseSubtag(value)] || "🏳️"
  }

  // The READ language — the glosses and every chrome string resolve in it, so
  // the badge and the language label repaint with it.
  applyLearnerLocale({ value }) {
    const locale = value || "en"
    this.setText("drillBadge", DRILL_BADGE[baseSubtag(locale)] || DRILL_BADGE.en)
    this.setText("languageLabel", languageName(this.fieldValue("target_language_code"), locale))
  }

  // The palette drives every ink on the frame: the frame + rows take
  // `--p-text`, the glosses take `--p-muted`, and the SLOT signal — box border,
  // step pill, CTA pill — takes `--p-accent`. Swapping the custom properties
  // repaints all of them without touching a single opacity.
  applyTheme({ value }) {
    if (!this.hasFrameTarget) return
    const palette = THEME_PALETTES[value] || THEME_PALETTES.notebook
    const f = this.frameTarget
    f.style.setProperty("--p-bg", palette.bg)
    f.style.setProperty("--p-surface", palette.surface)
    f.style.setProperty("--p-text", palette.text)
    f.style.setProperty("--p-muted", palette.muted)
    f.style.setProperty("--p-accent", palette.accent)
  }

  // -- typography / font-size / stroke --------------------------------------

  // DISPLAY role: the hook and the frame line (the hero of the frame). BODY
  // role: the grid rows, every gloss and the pills — the renderer sets the
  // rows in the body face for the same reason, they have to stay readable at
  // row height with N of them on screen.
  applyTypography() {
    const spec = TYPE_PAIR_SPECS[this.fieldValue("type_pair") || "default"]
    if (!spec) return
    applyTypographyToElements(
      { display: this.displayTargets(), body: this.bodyTargets() },
      spec,
      this.fieldValue("italic_override") || "auto",
    )
  }

  applyFontSize() {
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    // The frame LINE is sized as a unit (the slot box scales with the type it
    // sits in), so its two halves inherit rather than carrying their own size.
    applyFontSizeScale(this.displayTargets().map((el) => sizedPair(el, 13)), displayScale)
    applyFontSizeScale(this.bodyTargets().map((el) => sizedPair(el, 9)), bodyScale)
  }

  // Stroke rides the opted-in elements only — the hook + the two frame halves.
  // The badge, the pills, the glosses, the grid rows and the slot's own
  // contents sit on the theme surface and skip, mirroring the renderer's
  // applyStroke: false on those roles.
  applyStroke() {
    const targets = Array.from(
      this.element.querySelectorAll("[data-stroke-target]"),
    ).map((el) => ({
      element: el,
      basePx: parseFloat(el.dataset.strokeBasePx) || undefined,
      skip: el.dataset.strokeSkip === "true",
    }))
    if (!targets.length) return
    applyTextStrokeToElements(targets, {
      width: this.fieldValue("text_stroke_width"),
      color: this.fieldValue("text_stroke_color") || "#000000",
    })
  }

  applyTextShadow({ value }) {
    applyTextShadowToElements(
      Array.from(this.element.querySelectorAll("[data-stroke-target]")),
      value,
    )
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }

  // -- preview DOM helpers --------------------------------------------------

  learnerLocale() {
    return this.fieldValue("learner_locale") || "en"
  }

  displayTargets() {
    return [
      this.hasHookTextTarget ? this.hookTextTarget : null,
      this.hasFrameLineTarget ? this.frameLineTarget : null,
    ].filter(Boolean)
  }

  // Everything on the theme surface: the badge, the two pills, the frame gloss,
  // every grid row + row gloss, and the CTA pill.
  bodyTargets() {
    const singles = [ "drillBadge", "countPill", "stepPill", "frameGloss", "ctaText" ]
      .filter((name) => this[`has${capitalize(name)}Target`])
      .map((name) => this[`${name}Target`])
    return [ ...singles, ...this.optionTextTargets, ...this.optionGlossTargets ]
  }

  setText(targetName, value) {
    const has = `has${capitalize(targetName)}Target`
    if (this[has]) this[`${targetName}Target`].textContent = value
  }
}

// The structural slot — VideoConfig::SubstitutionDrillStrategy::PLACEHOLDER.
const PLACEHOLDER = "___"

function capitalize(name) {
  return name[0].toUpperCase() + name.slice(1)
}

// Ruby's String#partition: [before, separator, after], with all three always
// present. JS `split` would drop a trailing empty field, losing the after-half's
// existence — the same trap the builder's comment calls out.
function partition(text, separator) {
  const source = (text || "").toString()
  const at = source.indexOf(separator)
  if (at === -1) return [ source, "", "" ]
  return [ source.slice(0, at), separator, source.slice(at + separator.length) ]
}

// "en-GB" → "en". The flag and name lookups are keyed on the base subtag.
function baseSubtag(code) {
  return (code || "").toString().split("-")[0].toLowerCase()
}

// The label is localized in the VIEWER's language, mirroring
// LanguageNameResolver.call(code, locale: learner_locale) on the server — a
// Modo video (viewer L1 = es) reads "Inglés", not "English".
function languageName(code, learnerLocale) {
  const base = baseSubtag(code)
  const table = LANGUAGE_NAMES[baseSubtag(learnerLocale)] || LANGUAGE_NAMES.en
  return table[base] || LANGUAGE_NAMES.en[base] || base.toUpperCase()
}

// Every scaled element carries its renderer-relative base size in
// `data-base-font-size` (server-rendered), so the JS never restates the
// preview's size table — the fallback is only for a hand-built DOM.
function sizedPair(element, fallbackBasePx) {
  return { element, basePx: parseFloat(element.dataset.baseFontSize) || fallbackBasePx }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

// Authoring language spans (<en>…</en>, <es>…</es>) are never drawn. T4's
// authoring law says no payload carries one at all — the builder RAISES on a
// tag in the frame or a substitution — so stripping is purely so a hand-edited
// payload still draws clean.
function stripLanguageTags(text) {
  return (text || "").replace(/<\/?[a-zA-Z][a-zA-Z-]*>/g, "").replace(/\s+/g, " ").trim()
}

// All seven language-learning looks, including the two channel-locked ones —
// a channel-bound config CAN carry `cafecito` / `modo`, and repainting it as
// notebook would be the preview lying about the render.
const THEME_PALETTES = {
  "notebook":   { "bg": "#FAF7EC", "surface": "#FFFFFF", "text": "#1F2937", "muted": "#6B7280", "accent": "#E8A838" },
  "chalkboard": { "bg": "#1F2A24", "surface": "#243530", "text": "#F4F3EC", "muted": "#C5C2AE", "accent": "#FFE066" },
  "whiteboard": { "bg": "#F8FAFC", "surface": "#FFFFFF", "text": "#111827", "muted": "#4B5563", "accent": "#DC2626" },
  "cork_board": { "bg": "#C08B5C", "surface": "#FEF3C7", "text": "#1C1917", "muted": "#57534E", "accent": "#EAB308" },
  "minimal":    { "bg": "#FFFFFF", "surface": "#F8FAFC", "text": "#0F172A", "muted": "#64748B", "accent": "#6366F1" },
  "cafecito":   { "bg": "#F6EDDF", "surface": "#FFFBF2", "text": "#3B2A1E", "muted": "#6E5A47", "accent": "#C98A2D" },
  "modo":       { "bg": "#1E2733", "surface": "#28323F", "text": "#F2F5F9", "muted": "#A8B4C2", "accent": "#FFB020" },
}

// The franchise pill, mirroring `niche_templates.substitution_drill.drill_badge`
// in the three shipped locale files. Seen, never heard.
const DRILL_BADGE = {
  "en": "SPEED DRILL",
  "es": "PRÁCTICA RÁPIDA",
}

// Mirrors config/locales/language_names.{en,es}.yml for the languages the
// content step offers. An unknown pair falls back to the English name, then to
// the upper-cased code — the same degrade the server's `default:` gives.
const LANGUAGE_NAMES = {
  en: {
    "ar": "Arabic", "de": "German", "en": "English", "es": "Spanish", "fr": "French",
    "hi": "Hindi", "it": "Italian", "ja": "Japanese", "ko": "Korean", "nl": "Dutch",
    "pl": "Polish", "pt": "Portuguese", "ru": "Russian", "sv": "Swedish", "th": "Thai",
    "tr": "Turkish", "vi": "Vietnamese", "zh": "Chinese",
  },
  es: {
    "ar": "Árabe", "de": "Alemán", "en": "Inglés", "es": "Español", "fr": "Francés",
    "hi": "Hindi", "it": "Italiano", "ja": "Japonés", "ko": "Coreano", "nl": "Neerlandés",
    "pl": "Polaco", "pt": "Portugués", "ru": "Ruso", "sv": "Sueco", "th": "Tailandés",
    "tr": "Turco", "vi": "Vietnamita", "zh": "Chino",
  },
}

const FLAGS = {
  "ar": "🇸🇦", "de": "🇩🇪", "en": "🇬🇧", "es": "🇪🇸", "fr": "🇫🇷",
  "hi": "🇮🇳", "it": "🇮🇹", "ja": "🇯🇵", "ko": "🇰🇷", "nl": "🇳🇱",
  "pl": "🇵🇱", "pt": "🇵🇹", "ru": "🇷🇺", "sv": "🇸🇪", "th": "🇹🇭",
  "tr": "🇹🇷", "vi": "🇻🇳", "zh": "🇨🇳",
}
